import React, { useContext } from "react";
import Avatar from "../primitives/avatar";
import * as Form from "@radix-ui/react-form";
import Link from "next/link";
import Icon from "../primitives/icon";
import { BettorBasic, DiscussionComment } from "common";
import { createProfileSlug } from "@/utils/url";
import { getProfile } from "@/utils/bettor";
import { useUser } from "@clerk/nextjs";
import { AuthContext } from "@/app/(public)/drawer";
import { LoadingCircle } from "../primitives/loading";
import { useBettor } from "@/utils/providers/bettor";
import { useCooldown } from "@/utils/providers/cooldown";

interface CommentFormProps {
  gameId: number;
  replyingTo?: number;
  disabled?: boolean;
  setReplying?: React.Dispatch<React.SetStateAction<boolean>>;
  setJustReplied?: React.Dispatch<React.SetStateAction<boolean>>;
}
export default function CommentForm(props: CommentFormProps) {
  const { bettor } = useBettor();
  const { isCooldownActive, countdown, startCooldown } = useCooldown();
  const [message, setMessage] = React.useState<string>("");
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const { setAuth } = useContext(AuthContext);
  const disabled = !message || submitted || props.disabled || isCooldownActive;
  const maxLength = 200;


  const handleSubmit = React.useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const comment: Partial<DiscussionComment> = {
      gameID: props.gameId,
      comment: message,
      authorID: bettor?.info.clerk_id,
      parentCommentID: props.replyingTo ? props.replyingTo : undefined
    }
    console.log("replying comment:", comment);
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/api/discussions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          cache: "no-store",
          body: JSON.stringify(comment),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit feedback");
      }

      const result = await response.json();
      console.log("Comment submitted:", result);
      setMessage("");
    } catch (err) {
      console.error(err);
      setError("Failed to submit comment. Please try again.");
    } finally {
      setSubmitting(false);
      setSubmitted(true);
      setMessage("");
      startCooldown(30000);
      if (props.setReplying) props.setReplying(false);
      if (props.setJustReplied) props.setJustReplied(true);
    }
  }, [props.gameId, message, bettor, props.replyingTo]);

  function handleMessage(event: React.ChangeEvent<HTMLInputElement>) {
    setSubmitted(false);
    setMessage(event.target.value);
  }

  if (!bettor) {
    return (
      <button
        className="w-full"
        onClick={() => {
          setAuth("register");
        }}
      >
        <div className="flex flex-row gap-2 w-full items-center pt-2">
          <Avatar />
          <input
            type="text"
            required
            placeholder={`Add a ${props.replyingTo ? "Reply" : "Comment"}`}
            className="outline-none w-full bg-surface-600 h-[44px] rounded-md text-decoration-primary text-decoration-primary px-3"
          ></input>
          <div
            className={`bg-surface-600 text-secondary-600 text-title-sm-bold flex flex-row justify-center items-center uppercase gap-1 h-[44px] min-w-[114px] rounded-md`}
          >
            {props.replyingTo ? "Reply" : "Comment"}
            <Icon
              name="arrow-right"
              size="sm"
              className={`fill-secondary-600
              `}
            />
          </div>
        </div>
      </button>
    );
  }
  return (
    <Form.Root
      onSubmit={handleSubmit}
      className="mt-1 flex flex-row w-full gap-2 items-center"
    >
      {bettor ? (
        <Link href={createProfileSlug(bettor.info.display_name)}>
          <Avatar img={bettor.info.avatar_url}></Avatar>
        </Link>
      ) : (
        <div className="flex gap-2 items-center justify-center">
          <div className="w-8 h-8 flex justify-center items-center bg-secondary-400 rounded-full animate-pulse"></div>
        </div>
      )}
      <Form.Field name="message" className="w-full relative group">
        <Form.Label />

        <Form.Control
            value={message}
            onChange={handleMessage}
            type="text"
            required
            placeholder={isCooldownActive ? `30s cooldown...` : `Add a ${props.replyingTo ? "Reply" : "Comment"}`}
            maxLength={maxLength}
            className="outline-none w-full bg-surface-600 h-[44px] rounded-md text-decoration-primary text-decoration-primary pl-3 pr-14">
        </Form.Control>
          <div className="absolute h-full right-0 top-0 bottom-0 pr-3 text-secondary-500 text-label-medium pt-0.5 flex items-center">{message.length}/{maxLength}</div>
      </Form.Field>
       <Form.Submit
        disabled={disabled}
        className="btn btn-md btn-filled-primary md:min-w-[114px]"
      >
      {/* <Form.Submit
        disabled={disabled}
        className={`${
          disabled ? "bg-surface-600 text-secondary-600" : "bg-primary"
        } text-title-sm-bold flex flex-row justify-center items-center uppercase gap-1 h-[44px] min-w-[48px] md:min-w-[114px] rounded-md`}
      > */}
        {isCooldownActive && countdown > 0 ? (
          <div className="flex items-center gap-2 justify-center w-full">
          <span className="block">
              {countdown}
            </span>
            <Icon name="clock" size="sm" className="fill-secondary-600 hidden md:block" />
          </div>
        ) : submitted ? (
          <>
            <span className="hidden md:block">
              {props.replyingTo ? "Replied" : "Commented"}
            </span>
            <Icon name="checkmark" size="sm" className="fill-secondary-600" />
          </>
        ) : submitting ? (
          <>
            <span className="hidden md:block">
              {props.replyingTo ? "Reply" : "Comment"}
            </span>
            <LoadingCircle size={16} stroke={10} />
          </>
        ) : (
          <>
            <span className="hidden md:block">
              {props.replyingTo ? "Reply" : "Comment"}
            </span>
            <Icon
              name="arrow-right"
              size="sm"
              className={`${
                (!message || disabled) ? "fill-secondary-600" : "fill-secondary-300"
              }`}
            />
          </>
        )}
      </Form.Submit>
    </Form.Root>
  );
}
